/**
 * TasksPage — Admin view of all generated inspection form instances.
 *
 * Lists every inspection form (across schedules and one-off assignments) and lets an admin
 * assign/reassign each one to an operator (by email — the key the operator "My Tasks" screen
 * queries by) and open it for viewing. Listing also triggers on-the-fly generation of any due
 * schedules on the backend (GET /api/forms runs generateDue() first), so newly-due forms appear
 * without a background poller.
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Box2 from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';

import {
  FilterableDataTable,
  DrawerPanel,
  PageContainer,
} from '../../components/shared';
import type { FilterConfig, RowAction, DrawerAction } from '../../components/shared';
import {
  InspectionFormsApi,
  InspectionSchedulesApi,
  FormGenerationApi,
  MachineControllerApi,
  AdminControllerApi,
  ChecklistTemplateControllerApi,
} from '../../api-generated/apis';
import type { CognitoUser, Machine } from '../../api-generated/models';
import { apiConfig } from '../../api-generated/apiConfig';
import { useNotification } from '../../providers/NotificationProvider';
import { formatApiDate, formatApiDateTime } from '../../utils/dateFormat';

const formsApi = new InspectionFormsApi(apiConfig);
const schedulesApi = new InspectionSchedulesApi(apiConfig);
const generationApi = new FormGenerationApi(apiConfig);
const machineApi = new MachineControllerApi(apiConfig);
const adminApi = new AdminControllerApi(apiConfig);
const templateApi = new ChecklistTemplateControllerApi(apiConfig);

// ─── Types ───────────────────────────────────────────────────────────────────

type FormStatus = 'PENDING' | 'IN_PROGRESS' | 'OVERDUE' | 'COMPLETED' | 'CANCELLED';

interface TaskRow {
  id: string;
  templateId: string;
  templateName: string;
  machineId: string;
  machineName: string;
  status: FormStatus;
  assignedOperatorId: string;
  dueAt: string;
  generatedAt: string;
}

interface AssignFormData {
  assignedOperatorId: string;
  dueAt: string;
}

interface UpcomingRow {
  id: string; // synthetic: scheduleId + occurrenceAt
  scheduleId: string;
  templateId: string;
  templateName: string;
  machineId: string;
  machineName: string;
  frequency: string;
  occurrenceAt: string;
  status: 'OVERDUE' | 'DUE_NOW' | 'UPCOMING';
}

const INITIAL_ASSIGN: AssignFormData = { assignedOperatorId: '', dueAt: '' };

const STATUS_OPTIONS: { value: FormStatus; label: string }[] = [
  { value: 'PENDING', label: 'Pending' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'OVERDUE', label: 'Overdue' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

function statusColor(
  status?: string,
): 'default' | 'warning' | 'info' | 'success' | 'error' {
  switch (status) {
    case 'PENDING':
      return 'default';
    case 'IN_PROGRESS':
      return 'info';
    case 'OVERDUE':
      return 'error';
    case 'COMPLETED':
      return 'success';
    case 'CANCELLED':
      return 'warning';
    default:
      return 'default';
  }
}

function upcomingStatusColor(status?: string): 'default' | 'warning' | 'info' | 'error' {
  switch (status) {
    case 'OVERDUE':
      return 'error';
    case 'DUE_NOW':
      return 'warning';
    case 'UPCOMING':
      return 'info';
    default:
      return 'default';
  }
}

function formatDate(value?: unknown): string {
  return formatApiDate(value);
}

function formatDateTime(value?: unknown): string {
  return formatApiDateTime(value);
}

// ─── Component ───────────────────────────────────────────────────────────────

const TasksPage: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const notification = useNotification();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [assignData, setAssignData] = useState<AssignFormData>(INITIAL_ASSIGN);
  const [editingTask, setEditingTask] = useState<TaskRow | null>(null);
  // When assigning an upcoming occurrence we must materialize it first; track that in-flight state.
  const [materializing, setMaterializing] = useState(false);

  // ─── Queries ───────────────────────────────────────────────────────────

  const tasksQuery = useQuery<any[]>({
    queryKey: ['tasks', page, pageSize],
    // getAllForms returns an array; the typed deserializer can collapse it, so read raw JSON.
    queryFn: async () => {
      const response = await formsApi.getAllFormsRaw({});
      const data = await response.raw.json();
      return Array.isArray(data) ? data : data?.content ?? [];
    },
  });

  const upcomingQuery = useQuery<any[]>({
    queryKey: ['upcoming', 7],
    queryFn: async () => {
      const response = await schedulesApi.getUpcomingRaw({ days: 7 });
      const data = await response.raw.json();
      return Array.isArray(data) ? data : data?.content ?? [];
    },
  });

  const usersQuery = useQuery<CognitoUser[]>({
    queryKey: ['admin-users'],
    queryFn: () => adminApi.getAllUsers(),
    staleTime: 60_000,
  });

  const machinesQuery = useQuery<Machine[]>({
    queryKey: ['machines-options'],
    queryFn: () => machineApi.getAllMachines(),
    staleTime: 60_000,
  });

  const machineOptions = useMemo(
    () =>
      (machinesQuery.data ?? []).map((m: Machine) => ({
        id: (m as any).id ?? '',
        name: (m as any).name ?? '',
      })),
    [machinesQuery.data],
  );

  const templatesQuery = useQuery<any[]>({
    queryKey: ['templates-options'],
    queryFn: () => templateApi.getAllTemplates({}) as any,
    staleTime: 60_000,
  });

  const templateOptions = useMemo(
    () =>
      (templatesQuery.data ?? []).map((t: any) => ({ id: t.id ?? '', name: t.name ?? '' })),
    [templatesQuery.data],
  );

  const templateName = useCallback(
    (id: string) =>
      templateOptions.find((t: { id: string; name: string }) => t.id === id)?.name ?? id ?? '',
    [templateOptions],
  );

  const taskRows: TaskRow[] = useMemo(() => {
    const raw = tasksQuery.data;
    const items: any[] = Array.isArray(raw) ? raw : raw?.content ?? [];
    return items.map((item: any) => ({
      id: item.id ?? '',
      templateId: item.templateId ?? '',
      templateName: templateName(item.templateId ?? ''),
      machineId: item.machineId ?? '',
      machineName: machineOptions.find((m: { id: string; name: string }) => m.id === item.machineId)?.name ?? item.machineId ?? '',
      status: item.status ?? 'PENDING',
      assignedOperatorId: item.assignedOperatorId ?? '',
      dueAt: item.dueAt ?? '',
      generatedAt: item.generatedAt ?? '',
    }));
  }, [tasksQuery.data, machineOptions, templateName]);

  const upcomingRows: UpcomingRow[] = useMemo(() => {
    const raw = upcomingQuery.data;
    const items: any[] = Array.isArray(raw) ? raw : raw?.content ?? [];
    return items.map((item: any) => ({
      id: `${item.scheduleId ?? ''}#${item.occurrenceAt ?? ''}`,
      scheduleId: item.scheduleId ?? '',
      templateId: item.templateId ?? '',
      templateName: templateName(item.templateId ?? ''),
      machineId: item.machineId ?? '',
      machineName:
        machineOptions.find((m: { id: string; name: string }) => m.id === item.machineId)?.name ??
        item.machineId ??
        '',
      frequency: item.frequency ?? '',
      occurrenceAt: item.occurrenceAt ?? '',
      status: item.status ?? 'UPCOMING',
    }));
  }, [upcomingQuery.data, machineOptions, templateName]);

  // ─── Mutations ─────────────────────────────────────────────────────────

  const reassignMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: AssignFormData }) =>
      formsApi.reassignForm({
        id,
        reassignFormRequest: {
          assignedOperatorId: data.assignedOperatorId,
          dueAt: data.dueAt ? new Date(data.dueAt).toISOString() : undefined,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['myForms'] });
      handleCloseDrawer();
    },
  });

  // ─── Handlers ──────────────────────────────────────────────────────────

  const handleOpenAssign = useCallback((task: TaskRow) => {
    setEditingTask(task);
    setAssignData({
      assignedOperatorId: task.assignedOperatorId || '',
      dueAt: task.dueAt ? new Date(task.dueAt).toISOString().split('T')[0] : '',
    });
    setDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setEditingTask(null);
    setAssignData(INITIAL_ASSIGN);
  }, []);

  // Assign an upcoming (not-yet-materialized) occurrence: generate the form for its schedule,
  // then open the assign drawer on the resulting task. Idempotent server-side (an existing open
  // PENDING form is returned rather than duplicated).
  const handleAssignUpcoming = useCallback(
    async (row: UpcomingRow) => {
      setMaterializing(true);
      try {
        // Use the raw response: generateForm returns an empty body (200/204) when generation is
        // skipped (e.g. the template has no PUBLISHED version), which would make .value() throw a
        // JSON parse error. Read text ourselves and handle the empty case gracefully.
        const raw = await generationApi.generateFormRaw({ id: row.scheduleId });
        const httpRes = raw.raw;
        const text = await httpRes.text();

        if (!httpRes.ok) {
          notification.showError(
            text
              ? `Could not create task: ${text}`
              : `Could not create task (HTTP ${httpRes.status}).`,
          );
          return;
        }
        if (!text || text.trim() === '') {
          notification.showWarning(
            'No task was created for this schedule. Its template may not have a published version yet.',
          );
          return;
        }

        const form = JSON.parse(text);
        queryClient.invalidateQueries({ queryKey: ['tasks'] });
        queryClient.invalidateQueries({ queryKey: ['upcoming'] });

        if (form && form.id) {
          setEditingTask({
            id: form.id,
            templateId: form.templateId ?? row.templateId,
            templateName: row.templateName,
            machineId: form.machineId ?? row.machineId,
            machineName: row.machineName,
            status: (form.status ?? 'PENDING') as FormStatus,
            assignedOperatorId: form.assignedOperatorId ?? '',
            dueAt: form.dueAt ?? row.occurrenceAt ?? '',
            generatedAt: form.generatedAt ?? '',
          });
          setAssignData({
            assignedOperatorId: form.assignedOperatorId ?? '',
            dueAt: row.occurrenceAt ? new Date(row.occurrenceAt).toISOString().split('T')[0] : '',
          });
          setDrawerOpen(true);
        } else {
          notification.showWarning('No task was created for this schedule.');
        }
      } catch (err) {
        notification.showError(
          err instanceof Error ? `Could not create task: ${err.message}` : 'Could not create task.',
        );
      } finally {
        setMaterializing(false);
      }
    },
    [queryClient, notification],
  );

  const handleSubmitAssign = useCallback(() => {
    if (!editingTask || !assignData.assignedOperatorId) return;
    reassignMutation.mutate({ id: editingTask.id, data: assignData });
  }, [editingTask, assignData, reassignMutation]);

  // ─── Table config ──────────────────────────────────────────────────────

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'templateName', headerName: 'Template', flex: 1, minWidth: 140 },
      { field: 'machineName', headerName: 'Machine', flex: 1, minWidth: 140 },
      {
        field: 'assignedOperatorId',
        headerName: 'Assigned To',
        flex: 1,
        minWidth: 160,
        renderCell: (params) =>
          params.value ? (
            params.value
          ) : (
            <Chip label="Unassigned" color="warning" size="small" variant="outlined" />
          ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (params) => (
          <Chip label={params.value} color={statusColor(params.value)} size="small" />
        ),
      },
      {
        field: 'dueAt',
        headerName: 'Due',
        width: 120,
        renderCell: (params) => formatDate(params.value),
      },
      {
        field: 'generatedAt',
        headerName: 'Generated',
        width: 120,
        renderCell: (params) => formatDate(params.value),
      },
    ],
    [],
  );

  const filters: FilterConfig[] = useMemo(
    () => [
      {
        field: 'status',
        label: 'Status',
        type: 'select' as const,
        options: STATUS_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
      },
      {
        field: 'machine',
        label: 'Machine',
        type: 'select' as const,
        options: machineOptions.map((m: { id: string; name: string }) => ({ value: String(m.id), label: m.name })),
      },
      {
        field: 'assignedOperatorId',
        label: 'Assignment',
        type: 'select' as const,
        options: [
          { value: '__assigned', label: 'Assigned' },
          { value: '__unassigned', label: 'Unassigned' },
        ],
      },
    ],
    [machineOptions],
  );

  const upcomingColumns: GridColDef[] = useMemo(
    () => [
      { field: 'templateName', headerName: 'Template', flex: 1, minWidth: 140 },
      { field: 'machineName', headerName: 'Machine', flex: 1, minWidth: 140 },
      { field: 'frequency', headerName: 'Frequency', width: 120 },
      {
        field: 'occurrenceAt',
        headerName: 'When',
        width: 180,
        renderCell: (params) => formatDateTime(params.value),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (params) => {
          const label =
            params.value === 'DUE_NOW' ? 'Due now' : params.value === 'OVERDUE' ? 'Overdue' : 'Upcoming';
          return <Chip label={label} color={upcomingStatusColor(params.value)} size="small" />;
        },
      },
    ],
    [],
  );

  const upcomingActions: RowAction<UpcomingRow>[] = useMemo(
    () => [
      {
        label: 'Assign (create task)',
        icon: AssignmentIndIcon,
        onClick: (row: UpcomingRow) => {
          void handleAssignUpcoming(row);
        },
      },
    ],
    [handleAssignUpcoming],
  );

  const rowActions: RowAction<TaskRow>[] = useMemo(
    () => [
      {
        label: 'Assign / Reassign',
        icon: AssignmentIndIcon,
        onClick: handleOpenAssign,
      },
      {
        label: 'View',
        icon: VisibilityIcon,
        onClick: (task: TaskRow) => navigate(`/assignments/${task.id}`),
      },
    ],
    [handleOpenAssign, navigate],
  );

  const isTerminal = editingTask?.status === 'COMPLETED' || editingTask?.status === 'CANCELLED';

  const drawerActions: DrawerAction[] = useMemo(
    () => [
      { label: 'Cancel', variant: 'outlined' as const, onClick: handleCloseDrawer, testId: 'task-assign-cancel' },
      {
        label: 'Assign',
        variant: 'contained' as const,
        onClick: handleSubmitAssign,
        disabled: !assignData.assignedOperatorId || reassignMutation.isPending || isTerminal,
        testId: 'task-assign-confirm',
      },
    ],
    [handleCloseDrawer, handleSubmitAssign, assignData.assignedOperatorId, reassignMutation.isPending, isTerminal],
  );

  const userOptions = useMemo(
    () =>
      (usersQuery.data ?? [])
        .filter((u: CognitoUser) => !!u.email)
        .map((u: CognitoUser) => {
          const name = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
          return { value: u.email as string, label: name ? `${name} (${u.email})` : (u.email as string) };
        }),
    [usersQuery.data],
  );

  // ─── Render ────────────────────────────────────────────────────────────

  return (
    <PageContainer title="Tasks" subtitle="Assign and track inspection tasks">
      <FilterableDataTable<TaskRow>
        rows={taskRows}
        columns={columns}
        loading={tasksQuery.isLoading}
        totalCount={taskRows.length}
        searchPlaceholder="Search tasks…"
        filters={filters}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        actions={rowActions}
        testIdPrefix="tasks"
      />

      <Box2 sx={{ mt: 4 }}>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Upcoming (next 7 days)
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Forecast from active schedules. Assigning creates the task now so it can be done early.
        </Typography>
        <FilterableDataTable<UpcomingRow>
          rows={upcomingRows}
          columns={upcomingColumns}
          loading={upcomingQuery.isLoading || materializing}
          totalCount={upcomingRows.length}
          searchPlaceholder="Search upcoming…"
          page={0}
          pageSize={25}
          onPageChange={() => {}}
          onPageSizeChange={() => {}}
          actions={upcomingActions}
        />
      </Box2>

      <DrawerPanel
        open={drawerOpen}
        onClose={handleCloseDrawer}
        title={editingTask?.assignedOperatorId ? 'Reassign Task' : 'Assign Task'}
        width="narrow"
        actions={drawerActions}
        loading={reassignMutation.isPending}
        testId="task-assign-form"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <TextField
            select
            label="Operator"
            value={assignData.assignedOperatorId}
            onChange={(e) =>
              setAssignData((prev) => ({ ...prev, assignedOperatorId: e.target.value }))
            }
            fullWidth
            required
            disabled={usersQuery.isLoading || isTerminal}
            helperText={isTerminal ? 'This task is closed and cannot be reassigned.' : undefined}
            error={isTerminal}
            SelectProps={{ SelectDisplayProps: { 'data-testid': 'task-assign-operator' } as any }}
          >
            {userOptions.map((u: { value: string; label: string }) => (
              <MenuItem key={u.value} value={u.value}>
                {u.label}
              </MenuItem>
            ))}
          </TextField>

          <DatePicker
            label="Due Date (optional)"
            value={assignData.dueAt ? dayjs(assignData.dueAt) : null}
            onChange={(value: Dayjs | null) =>
              setAssignData((prev) => ({
                ...prev,
                dueAt: value && value.isValid() ? value.format('YYYY-MM-DD') : '',
              }))
            }
            format="DD MMM YYYY"
            disablePast
            disabled={isTerminal}
            slotProps={{
              textField: {
                fullWidth: true,
                inputProps: { readOnly: true },
                helperText: 'Pick a due date, or leave empty for no due date',
              },
              actionBar: { actions: ['clear', 'today', 'accept'] },
            }}
          />
        </Box>
      </DrawerPanel>
    </PageContainer>
  );
};

export default TasksPage;
